import type { TimeReportConfig, TimeReportGroupByLevel } from '@hygge/shared';
import type { TimeReportRunResult } from '@/lib/time-entries';
import { cn } from '@/lib/utils';
import { formatHours } from '../lib/report-print';
import {
  REPORT_METRICS,
  blankCell,
  headedGroups,
  levelLabel,
  rowLabel,
  underHeader,
} from '../lib/report-builder-state';

/**
 * A built report as a table: one column per grouping level, one per metric, a
 * group's total above its rows, and the report's own total in the footer.
 *
 * Shared by the Builder and the Reports screen's summarized shapes, so a
 * grouping reads the same wherever it was assembled. The PDF renders these
 * rows a second time; which grouping cell is printed empty is `blankCell`,
 * called from both, and which cell is indented (a nested row's inner one) is
 * spelled in each. The residual's italics and the flag in the key are this
 * table's own. [HW-877]
 */
export function ResultTable({
  rows,
  groupBy,
  metrics,
  totalSeconds,
}: {
  rows: TimeReportRunResult['rows'];
  groupBy: TimeReportGroupByLevel[];
  metrics: TimeReportConfig['metrics'];
  totalSeconds: number;
}) {
  // Rows under a total line do not repeat the group's name — the reader has
  // just read it in the total line, one row up — and their own label is
  // indented instead, as Tempo prints a person once with the projects beneath.
  const headed = headedGroups(rows);
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/40">
            {groupBy.map((level) => (
              <th
                key={level}
                className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground"
              >
                {levelLabel(level)}
              </th>
            ))}
            {metrics.map((metric) => (
              <th
                key={metric}
                className="px-4 py-2.5 text-right text-xs font-medium text-muted-foreground"
              >
                {REPORT_METRICS.find((m) => m.metric === metric)?.label ?? metric}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const nested = underHeader(row, headed);
            return (
              <tr
                // The flag is part of the key so that what makes two rows
                // distinct is stated rather than incidental: a group's total and
                // its residual row key off the same outer id, and they differ
                // today only in a trailing separator ("p1" against "p1•") — one
                // change to how keys are joined away from colliding.
                key={`${row.subtotal ? 'sum' : 'row'}:${row.keys.join('•')}`}
                className={cn(
                  'border-b border-border last:border-0',
                  // A group's own total repeats hours that are also in the rows
                  // below it. Unless it LOOKS like their total it reads as one
                  // more row, and the column appears not to add up. [HW-877]
                  row.subtotal && 'bg-muted/30 font-medium'
                )}
              >
                {groupBy.map((level, index) => {
                  const blank = blankCell(row, nested, index);
                  return (
                    <td
                      key={level}
                      className={cn(
                        'px-4 py-2',
                        nested && index === 1 && 'pl-8',
                        // A residual bucket is a remainder, not a competitor — it
                        // reads as one rather than looking like a missing value. Its
                        // total line stays plain: that row is already set off, and
                        // under it the name appears nowhere else.
                        !row.subtotal && !row.label[index] && 'text-muted-foreground italic'
                      )}
                    >
                      {blank ? '' : rowLabel(level, row.label[index] ?? '')}
                    </td>
                  );
                })}
                {metrics.map((metric) => (
                  <td key={metric} className="px-4 py-2 text-right tabular-nums">
                    {metric === 'hours'
                      ? formatHours(row.totalSeconds)
                      : metric === 'share'
                        ? `${row.sharePercent}%`
                        : formatHours(row.dailyAverageSeconds)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="border-t border-border bg-muted/40 font-medium">
            <td className="px-4 py-2.5" colSpan={groupBy.length}>
              Total
            </td>
            {metrics.map((metric) => (
              <td key={metric} className="px-4 py-2.5 text-right tabular-nums">
                {metric === 'hours' ? formatHours(totalSeconds) : metric === 'share' ? '100%' : ''}
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
